import classNames from 'classnames'
// @ts-ignore
import { DomElement } from 'htmlparser2'
import Script from 'next/script'
import { Fragment, useEffect, useRef, useState } from 'react'
import BSCard from 'react-bootstrap/Card'
import ReactHtmlParser, { convertNodeToElement } from 'react-html-parser'

import Card from '@/components/Card/Card'
import CardHover from '@/components/CardHover/CardHover'
import Container from '@/components/Container/Container'
import { ExplicitHead } from '@/components/DefaultHead/DefaultHead'
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary'
import Main from '@/components/Main/Main'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import Deck from '@/components/Wordpress/Deck/Deck'
import { PostType } from '@/types/wordpress'
import useDirectories from '@/utils/useDirectories'

import Author from '../ArticlePreview/Author'
import Date from '../ArticlePreview/Date'
import FeaturedImage from '../ArticlePreview/FeaturedImage'

import styles from './ArticlePage.module.scss'
import CSBEmbed from './CSBEmbed'
import Disqus from './Disqus'
import Errors from './Errors'
import ReadMorePreview from './ReadMorePreview'

type Props = { enableAdhesion: boolean; post: PostType }

const ArticlePage = ({
  enableAdhesion,
  post: { author, content, databaseId, date, excerpt, featuredImageUrl, slug, tags, title },
}: Props) => {
  const { subpage } = useDirectories()
  const cardNamesRef = useRef<string[]>([])
  const errorsRef = useRef<string[]>([])
  const keyRef = useRef<number>(0)
  const [isClient, setIsClient] = useState(false)

  const isPreview = subpage === 'preview'
  const _key = () => `${keyRef.current++}`

  const parseName = (name: string | undefined) => {
    if (!name) return ''
    const parsedName = name.replace('★', 'star').replace('†', 'dagger').replace('Φ', 'phi')
    if (isPreview) cardNamesRef.current.push(parsedName)
    return parsedName
  }

  const transform = (node: DomElement, i: number) => {
    const classList = node.attribs?.class?.split(' ') || []

    if (classList.includes('edhrecp__cards')) {
      // [ec]
      const key = _key()
      const children = node.children
        .filter(
          (child: DomElement) =>
            child.type === 'tag' && child.name === 'div' && child.attribs?.class?.includes('edhrecp__card-container'),
        )
        .map((child: DomElement, j: number) => {
          const name = parseName(child.attribs?.name)
          return (
            <Card
              isPreview={isPreview}
              key={`ec-${key}-${j}`}
              name={name}
              onlyBack={classList.includes('only-back')}
              onlyFront={classList.includes('only-front')}
              reverse={classList.includes('reverse')}
              sideBySide={classList.includes('side-by-side')}
              target='_blank'
            />
          )
        })
      return (
        <div className={styles.cards} key={`ec-${key}`}>
          {children}
        </div>
      )
    } else if (classList.includes('edhrecp__link')) {
      // [el]
      const key = _key()
      const children = node.children
        .filter(
          (child: DomElement) =>
            child.type === 'tag' && child.name === 'a' && child.attribs?.class === 'edhrecp__link-a',
        )
        .map((child: DomElement, j: number) => {
          const name = parseName(child.attribs?.content)
          return (
            <CardHover
              isPreview={isPreview}
              key={`el-${key}-${j}`}
              name={name}
              onlyBack={classList.includes('only-back')}
              onlyFront={classList.includes('only-front')}
              reverse={classList.includes('reverse')}
              sideBySide={classList.includes('side-by-side')}
              target='_blank'
            >
              {node.children[0].children[0]?.data}
            </CardHover>
          )
        })
      return (
        <span className={node.attribs?.class} key={`el-${key}}`}>
          {children}
        </span>
      )
    } else if (classList.includes('edhrecp__deck')) {
      // [ed]
      const key = _key()
      const children = node.children
        .filter(
          (child: DomElement) =>
            child.type === 'tag' && child.name === 'span' && child.attribs?.class === 'edhrecp__deck-s',
        )
        .map((child: DomElement, j: number) => {
          const deckKey = `ed-${key}-${j}`
          const deckTitle = child?.attribs.name
          if (isClient && title.length + deckTitle.length >= 125) {
            errorsRef.current.push(
              `Generated Archidekt deck title too long.  Please shorten the deck title, or the post title.`,
            )
          }
          return (
            <ErrorBoundary key={deckKey} tag={`${slug}-${deckKey}`}>
              <Deck
                archidekt_link={child?.attribs?.archidekt_link}
                rawDeck={child.attribs?.cards}
                slug={slug}
                title={deckTitle}
              />
            </ErrorBoundary>
          )
        })
      return (
        <div className={node.attribs?.class} key={`ed-${key}`}>
          {children}
        </div>
      )
    } else if (classList.includes('mana-container')) {
      // [mana]
      const key = _key()
      return (
        <span className={styles.mana} key={`mana-${key}`}>
          {convertNodeToElement(node.children[0], i, transform)}
        </span>
      )
    } else if (node.type === 'tag' && node.attribs?.class?.includes('wp-embedded-content')) {
      // WP embeds
      if (node.name === 'blockquote') {
        const match = node?.children[0]?.children[0].attribs.href
        if (!match || !match.startsWith('https://edhrec.com')) return convertNodeToElement(node, i, transform)
        const subSlug = match.replace('https://edhrec.com/articles/', '').replace(/\/$/, '')
        return <ReadMorePreview slug={subSlug} />
      } else if (node.name === 'iframe') {
        return null
      }
    } else if (node.type === 'tag' && node.name === 'a') {
      // Misc links
      const key = _key()
      return (
        <OutboundLink key={`a-${key}}`} url={node.attribs.href}>
          {node.children.map((child: DomElement, j: number) => (
            <Fragment key={`a-${key}}-${j}`}>{convertNodeToElement(child, j, transform)}</Fragment>
          ))}
        </OutboundLink>
      )
    } else if (node.name === 'noscript') {
      // <noscript> (iframes)
      return node.children.map((child: DomElement) => convertNodeToElement(child, i, transform))
    } else if (node.type === 'script' && node.name === 'script' && isClient) {
      // <script>
      const key = _key()
      if (node.attribs.src) {
        if (node.attribs.src.startsWith('https://commanderspellbook.com/embed.js?v=1')) {
          return <CSBEmbed id={node.parent.attribs.id} key={`s-${key}`} url={node.attribs.src} />
        } else if (node.attribs.src.startsWith('https://secure.polldaddy.com/p/')) {
          const match = node.attribs.src.match(/https:\/\/secure.polldaddy.com\/p\/(\d+)\.js/)
          return (
            <Fragment key={`s-${key}`}>
              <Script src={node.attribs.src} />
              <div className={styles.crowdsignal} id={`PDI_container${match[1]}`} />
            </Fragment>
          )
        } else {
          return <Script key={`s-${key}`} src={node.attribs.src} />
        }
      } else if (node.children.length) {
        return (
          <Script id={`s-${key}`} key={`s-${key}`}>
            {node.children[0].data}
          </Script>
        )
      } else {
        return (
          <script key={`s-${key}`} {...node.attribs}>
            {node.children.map((child: DomElement, j: number) => (
              <Fragment key={`s-${key}-${j}`}>{convertNodeToElement(child, j, transform)}</Fragment>
            ))}
          </script>
        )
      }
    }
  }

  useEffect(() => {
    if (!isClient) setIsClient(true)
  }, [])

  return (
    <Main>
      <Container article>
        <ExplicitHead
          description={excerpt}
          imageUrl={featuredImageUrl}
          keywords={tags?.map((tag) => tag.name).join(', ')}
          title={title}
        />
        <link
          href='https://edhrec.com/articles/wp-content/plugins/edhrec-plugin/build/styles.css'
          id='MyPluginStyles-css'
          media='all'
          rel='stylesheet'
          type='text/css'
        />
        {/* eslint-disable-next-line @next/next/no-css-tags*/}
        <link
          href='//cdn.jsdelivr.net/npm/mana-font@latest/css/mana.min.css'
          id='mana-font'
          rel='stylesheet'
          type='text/css'
        />
        <div className='d-flex flex-column gap-2'>
          {isPreview && <Errors cardNames={cardNamesRef.current} errors={errorsRef.current} />}
          {title && <h2 dangerouslySetInnerHTML={{ __html: title }} />}
          {(author || date) && (
            <div className='align-items-center d-flex gap-2'>
              {date && <Date dateString={date} />}
              <div className='text-secondary'>by</div>
              {author && <Author author={author} />}
            </div>
          )}
          {featuredImageUrl && <FeaturedImage height='auto' src={featuredImageUrl} title={title} width='auto' />}
        </div>
        <div className={styles.content}>{ReactHtmlParser(content, { transform })}</div>
        {databaseId && (
          <>
            <Disqus identifier={databaseId} slug={slug} title={title} />
            <BSCard className={classNames(styles.codeOfConduct, 'shadow-sm')}>
              <BSCard.Body>
                <BSCard.Title>EDHREC Code of Conduct</BSCard.Title>
                <BSCard.Text>
                  Your opinions are welcome. We love hearing what you think about <i>Magic</i>! We ask that you are
                  always respectful when commenting. Please keep in mind how your comments could be interpreted by
                  others. Personal attacks on our writers or other commenters will not be tolerated. Your comments may
                  be removed if your language could be interpreted as aggressive or disrespectful. You may also be
                  banned from writing further comments.
                </BSCard.Text>
              </BSCard.Body>
            </BSCard>
          </>
        )}
        {enableAdhesion && <div className='mvHomeAdhesion' />}
      </Container>
    </Main>
  )
}

export default ArticlePage
